<template>
  <aside
    class="fixed inset-y-0 left-0 z-50 w-64 bg-white border-r border-gray-200 transition-transform duration-300 ease-in-out"
    :class="{ '-translate-x-full': !isOpen, 'translate-x-0': isOpen }"
  >
    <div class="flex flex-col h-full">
      <!-- Logo et bouton de fermeture -->
      <div
        class="flex items-center justify-between p-4 border-b transition-all duration-300"
        :class="{
          'bg-brand border-brand':
            selectedBrand === 'hv' || selectedBrand === 'valtra',
          'bg-[#4B7526] border-[#4B7526]': selectedBrand === 'fendt',
        }"
      >
        <div class="relative flex-1">
          <button
            class="flex items-center justify-between focus:outline-none w-full"
            @click="isBrandMenuOpen = !isBrandMenuOpen"
          >
            <div class="w-40 h-12 flex items-center justify-center">
              <img
                :src="brandLogo"
                :alt="brandName"
                class="h-full w-auto object-contain transition-transform duration-200 hover:scale-105"
                :class="{
                  'brightness-0 invert':
                    selectedBrand === 'agco' ||
                    selectedBrand === 'hv' ||
                    selectedBrand === 'valtra' ||
                    selectedBrand === 'fendt',
                }"
              >
            </div>
            <svg
              class="w-5 h-5 ml-2"
              :class="{
                'text-white':
                  selectedBrand === 'agco' ||
                  selectedBrand === 'hv' ||
                  selectedBrand === 'valtra' ||
                  selectedBrand === 'fendt',
              }"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M19 9l-7 7-7-7"
              />
            </svg>
          </button>

          <!-- Menu déroulant des marques -->
          <div
            v-if="isBrandMenuOpen"
            class="absolute left-0 mt-2 w-56 bg-white rounded-md shadow-lg py-2 z-50"
          >
            <button
              v-for="brand in brands"
              :key="brand.value"
              class="w-full px-4 py-3 text-left hover:bg-gray-100 flex items-center justify-center transition-all duration-200 hover:shadow-inner"
              @click="selectBrand(brand.value)"
            >
              <div class="w-40 h-12 flex items-center justify-center">
                <img
                  :src="brand.logo"
                  :alt="brand.name"
                  class="h-full w-auto object-contain transition-transform duration-200 hover:scale-105"
                >
              </div>
            </button>
          </div>
        </div>
        <button
          class="p-2 rounded-lg lg:hidden"
          :class="{
            'hover:bg-brand/20 text-white':
              selectedBrand === 'agco' ||
              selectedBrand === 'hv' ||
              selectedBrand === 'valtra',
            'hover:bg-[#2E7D32]/20 text-white': selectedBrand === 'fendt',
          }"
          @click="toggleSidebar"
        >
          <svg
            class="w-6 h-6"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              v-if="isOpen"
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M6 18L18 6M6 6l12 12"
            />
            <path
              v-else
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M4 6h16M4 12h16M4 18h16"
            />
          </svg>
        </button>
      </div>

      <!-- Profil utilisateur -->
      <div class="p-4 border-b border-gray-200">
        <div class="relative">
          <button
            class="flex items-center space-x-3 w-full focus:outline-none"
            @click="isProfileMenuOpen = !isProfileMenuOpen"
          >
            <div
              class="w-20 h-20 rounded-full bg-gray-100 flex items-center justify-center overflow-hidden"
            >
              <template v-if="userLogoUrl">
                <img
                  :src="userLogoUrl"
                  :alt="user?.user_details?.name"
                  class="w-full h-full object-contain rounded-full bg-white"
                >
              </template>
              <template v-else>
                <svg
                  class="w-6 h-6 text-gray-400"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="1.5"
                    d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
                  />
                </svg>
              </template>
            </div>
            <div class="flex-1 min-w-0 text-left">
              <p class="text-sm font-medium text-gray-900 truncate">
                {{ user?.user_details?.name }}
              </p>
              <p class="text-sm text-gray-500 truncate">
                {{ user?.user_details?.email || user?.email }}
              </p>
              <!-- Indicateur de rôle (Fournisseur) -->
              <span
                v-if="user?.roles?.includes('ROLE_SUPPLIER')"
                class="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800 mt-1"
              >
                Fournisseur
              </span>
            </div>
            <svg
              class="w-4 h-4 text-gray-400"
              :class="{ 'transform rotate-180': isProfileMenuOpen }"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M19 9l-7 7-7-7"
              />
            </svg>
          </button>

          <!-- Backdrop blur -->
          <div
            v-show="isProfileMenuOpen"
            class="fixed inset-0 bg-black/30 backdrop-blur-sm transition-opacity duration-200"
            :class="{
              'opacity-100': isProfileMenuOpen,
              'opacity-0 pointer-events-none': !isProfileMenuOpen,
            }"
            @click="isProfileMenuOpen = false"
          />

          <!-- Menu déroulant du profil -->
          <div
            v-show="isProfileMenuOpen"
            class="absolute top-full left-0 w-full bg-white rounded-lg shadow-xl py-1 z-[60] transform transition-all duration-200 ease-out origin-top border border-gray-200"
            :class="{
              'scale-y-100 opacity-100': isProfileMenuOpen,
              'scale-y-0 opacity-0 pointer-events-none': !isProfileMenuOpen,
            }"
          >
            <router-link
              to="/profile"
              class="flex items-center px-4 py-3 text-sm text-gray-700 hover:bg-gray-50"
              @click="isProfileMenuOpen = false"
            >
              <UserIcon class="w-5 h-5 mr-3 text-gray-500" />
              Mon profil
            </router-link>
            <button
              class="flex items-center w-full px-4 py-3 text-sm text-gray-700 hover:bg-gray-50"
              @click="
                () => {
                  logout();
                  isProfileMenuOpen = false;
                }
              "
            >
              <svg
                class="w-5 h-5 mr-3 text-gray-500"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1"
                />
              </svg>
              Déconnexion
            </button>
          </div>
        </div>
      </div>

      <!-- Navigation -->
      <nav class="flex-1 p-4 space-y-1 overflow-y-auto">
        <template v-for="(item, index) in navigationItems" :key="index">
          <div
            v-if="item.type === 'separator'"
            class="my-4 border-t border-gray-200"
          />
          <router-link
            v-else-if="item.type === 'item' && item.href && !item.external"
            :to="item.href"
            class="flex items-center px-4 py-2 text-gray-700 rounded-lg hover:bg-gray-100"
            :class="{
              'bg-brand/10 text-brand': isActive(item.href),
              'text-brand': item.color === '#B70018',
            }"
            @click="handleNavigationClick(item.href)"
          >
            <component
              :is="item.icon"
              v-if="item.icon"
              class="w-5 h-5 mr-3"
              :class="
                item.color === '#B70018' ? 'text-brand' : 'text-gray-500'
              "
            />
            <span v-if="item.name">{{ item.name }}</span>
          </router-link>
          <a
            v-else-if="item.type === 'item' && item.href && item.external"
            :href="item.href"
            target="_blank"
            rel="noopener noreferrer"
            class="flex items-center px-4 py-2 text-gray-700 rounded-lg hover:bg-gray-100"
            :class="{
              'text-brand': item.color === '#B70018',
            }"
          >
            <component
              :is="item.icon"
              v-if="item.icon"
              class="w-5 h-5 mr-3"
              :class="
                item.color === '#B70018' ? 'text-brand' : 'text-gray-500'
              "
            />
            <span v-if="item.name">{{ item.name }}</span>
          </a>
        </template>
      </nav>
    </div>
  </aside>

  <!-- Overlay pour mobile -->
  <div
    v-if="isOpen"
    class="fixed inset-0 z-40 bg-black bg-opacity-50 lg:hidden"
    @click="toggleSidebar"
  />
</template>

<script setup lang="ts">
import { useAuthStore } from "@/stores/auth";
import { useBrandStore } from "@/stores/brand";
import { useCurrentUser, useCurrentBrand, useAuthToken, useUserLogo } from "@/utils/storage";
import {
  BuildingOfficeIcon,
  DocumentIcon,
  EnvelopeIcon,
  FolderIcon,
  HomeIcon,
  PhotoIcon,
  QuestionMarkCircleIcon,
  ShareIcon,
  ShieldCheckIcon,
  ShoppingBagIcon,
  UserIcon
} from "@heroicons/vue/24/outline";
import { computed, onMounted, onUnmounted, ref } from "@vue/runtime-core";
import { useRoute, useRouter } from "vue-router";
import { storeToRefs } from "pinia";

defineProps<{
  isOpen: boolean;
}>();

const emit = defineEmits<{
  (e: "toggle"): void;
}>();

const router = useRouter();
const route = useRoute();
const authStore = useAuthStore();
const brandStore = useBrandStore();
const { currentBrand } = storeToRefs(brandStore);

// ✅ Utiliser le système de storage réactif
const { userData: currentUserData } = useCurrentUser();
const { brand: reactiveCurrentBrand } = useCurrentBrand();
const { token: authToken } = useAuthToken();
const { logoUrl: userLogoUrl } = useUserLogo();

const user = computed(() => {
  return currentUserData.value || null;
});

// Utiliser le brand depuis le store réactif
const selectedBrand = currentBrand;
const isBrandMenuOpen = ref(false);
const isProfileMenuOpen = ref(false);

const brands = computed(() => {
  if (currentUserData.value) {
    const data = currentUserData.value as any;
    return (data.groups || []).map((group: any) => ({
      value: group.slug,
      name: group.name,
      logo: `/images/logo-${group.slug}.svg`,
    }));
  }
  return [];
});

interface NavigationItem {
  name?: string;
  href?: string;
  icon?: any;
  type?: "separator" | "item";
  color?: string;
  external?: boolean;
}

const navigationItems: NavigationItem[] = [
  { type: "item", name: "Tableau de bord", href: "/", icon: HomeIcon },
  { type: "item", name: "Mes documents", href: "/documents", icon: FolderIcon },
  {
    type: "item",
    name: "Documents AGCO",
    href: "/agco-documents",
    icon: FolderIcon,
    color: "#B70018",
  },
  {
    type: "item",
    name: "Mes produits",
    href: "/personal-photos",
    icon: PhotoIcon,
  },
  { type: "separator" },
  {
    type: "item",
    name: "Extranet Fournisseurs",
    href: "/extranet",
    icon: BuildingOfficeIcon,
    color: "#B70018",
  },
  { type: "separator" },
  {
    type: "item",
    name: "Bon de commande",
    href: "/order",
    icon: ShoppingBagIcon,
  },
  { type: "separator" },
  {
    type: "item",
    name: "Affiche (A4 - A3)",
    href: "/supports/affiche/nouveau",
    icon: DocumentIcon,
    color: "#B70018",
  },
  {
    type: "item",
    name: "Flyer A4 (Recto/Verso)",
    href: "/supports/flyer/nouveau",
    icon: DocumentIcon,
    color: "#B70018",
  },
  {
    type: "item",
    name: "Dépliant A4 (4 pages)",
    href: "/supports/depliant/nouveau",
    icon: DocumentIcon,
    color: "#B70018",
  },
  {
    type: "item",
    name: "Banderole",
    href: "/supports/banderole/nouveau",
    icon: DocumentIcon,
    color: "#B70018",
  },
  {
    type: "item",
    name: "E-mailing",
    href: "/supports/emailing/nouveau",
    icon: EnvelopeIcon,
    color: "#B70018",
  },
  {
    type: "item",
    name: "Réseaux sociaux",
    href: "/supports/visuel-reseaux/nouveau",
    icon: ShareIcon,
    color: "#B70018",
  },
  { type: "separator" },
  {
    type: "item",
    name: "Produits Fournisseurs",
    href: "/supplier-photos",
    icon: PhotoIcon,
    color: "#B70018",
  },
  {
    type: "item",
    name: "Produits AGCO",
    href: "/agco-photos",
    icon: PhotoIcon,
    color: "#B70018",
  },

  { type: "separator" },
  {
    type: "item",
    name: "Aide en ligne",
    href: "/help",
    icon: QuestionMarkCircleIcon,
  },
    {
    type: "item",
    name: "Mentions légales",
    href: "https://www.agcocorp.com/int/en/home/privacy-center/legal.html",
    icon: DocumentIcon,
    external: true,
  },
  {
    type: "item",
    name: "Politique des cookies",
    href: "https://www.agcocorp.com/content/websites/agcocorp-v2/int/en/home/privacy-center/cookie-policy/cookie-policy-fr.html",
    icon: ShieldCheckIcon,
    external: true,
  },
];

const selectBrand = async (brand: string) => {
  // Suppression des clés du localStorage
  const keysToRemove = [
    "brand_assets_cache",
    "cache_timestamp",
    "cached_providers",
    "catalogs_cache",
    "documents_cache",
    "helps",
    "operation_cache",
    "agco_documents_cache",
  ];

  keysToRemove.forEach((key) => {
    localStorage.removeItem(key);
  });

  // Mettre à jour via le store centralisé
  brandStore.setBrand(brand);

  if (authStore.user) {
    await authStore.setBrand(brand);
    isBrandMenuOpen.value = false;

    window.location.reload();
  }
};

// Modifier handleClickOutside pour gérer aussi le menu profil
const handleClickOutside = (event: MouseEvent) => {
  const target = event.target as HTMLElement;
  if (!target.closest(".relative")) {
    isBrandMenuOpen.value = false;
    isProfileMenuOpen.value = false;
  }
};

onMounted(() => {
  document.addEventListener("click", handleClickOutside);

  // Initialiser le brand si nécessaire
  if (!currentBrand.value) {
    brandStore.initialize();
  }

  // ✅ Vérifier si les user_details sont présents dans les données réactives
  if (currentUserData.value) {
    const parsedUser = currentUserData.value as any;
    if (!parsedUser.user_details) {
      // Si les user_details ne sont pas présents, les récupérer depuis l'API
      updateUserDetails();
      // Vérifier directement la réponse de l'API
      checkApiResponse();
    }
  }
});

onUnmounted(() => {
  document.removeEventListener("click", handleClickOutside);
});

const brandName = computed(() => {
  const selectedBrandValue = currentBrand.value;
  if (!selectedBrandValue) return "AGCO";

  // ✅ Utiliser les données réactives
  if (currentUserData.value) {
    const data = currentUserData.value as any;
    const group = data.groups?.find((g: any) => g.slug === selectedBrandValue);
    return group?.name || "AGCO";
  }
  return "AGCO";
});

const brandLogo = computed(() => {
  const selectedBrandValue = currentBrand.value;
  if (!selectedBrandValue) return "/images/logo-agco.svg";
  return `/images/logo-${selectedBrandValue}.svg`;
});

const isActive = (href: string) => {
  if (!href) return false;
  return route.path === href;
};

const toggleSidebar = () => {
  emit("toggle");
};

const logout = async () => {
  await authStore.logout();
  localStorage.clear();
  router.push("/login");
};

// Fonction pour mettre à jour les user_details
const updateUserDetails = async () => {
  try {
    await authStore.refreshUserData();
  } catch (error) {
    console.error("Erreur lors de la mise à jour des user_details:", error);
  }
};

// Fonction pour vérifier directement la réponse de l'API
const checkApiResponse = async () => {
  try {
    const apiUrl = import.meta.env.VITE_API_URL_FULL.endsWith("/")
      ? `${import.meta.env.VITE_API_URL_FULL}me`
      : `${import.meta.env.VITE_API_URL_FULL}/me`;

    // ✅ Utiliser le token réactif
    const response = await fetch(apiUrl, {
      credentials: "include",
      headers: {
        Authorization: `Bearer ${authToken.value}`,
        Accept: "application/json",
        "X-Requested-With": "XMLHttpRequest",
      },
    });

    if (response.ok) {
      const data = await response.json();

      if (data.user_details && currentUserData.value) {
        // ✅ Mettre à jour les données réactives
        const updatedUserData = {
          ...currentUserData.value,
          user_details: data.user_details
        };
        currentUserData.value = updatedUserData as any;
      }
    }
  } catch (error) {
    console.error("Erreur lors de la vérification de l'API:", error);
  }
};

const handleNavigationClick = (href: string) => {
  // Liste des chemins qui nécessitent un rechargement
  const pathsToReload = [
    "/supports/affiche/nouveau",
    "/supports/flyer/nouveau",
    "/supports/depliant/nouveau",
    "/supports/banderole/nouveau",
    "/supports/emailing/nouveau",
    "/supports/visuel-reseaux/nouveau",
  ];

  if (pathsToReload.includes(href)) {
    // Attendre que la navigation soit terminée avant de recharger
    setTimeout(() => {
      window.location.reload();
    }, 100);
  }
};
</script>
